/**
 * Telefon numarasını Türk formatına çevirir: "0 (542) 451 00 42"
 * @param phone - Ham telefon numarası
 * @returns Formatlanmış telefon numarası
 */
export const formatPhoneNumber = (phone: string): string => {
  // Sadece rakamları al
  const cleanNumber = phone.replace(/\D/g, '');
  
  // Boşsa boş döndür
  if (!cleanNumber) return '';
  
  // 0 ile başlamıyorsa 0 ekle
  const withZero = cleanNumber.startsWith('0') ? cleanNumber : '0' + cleanNumber;
  
  // Maksimum 11 hane (0 + 10 hane)
  const limited = withZero.slice(0, 11);
  
  // Formatla
  if (limited.length <= 1) return limited;
  if (limited.length <= 4) return `${limited[0]} (${limited.slice(1)})`;
  if (limited.length <= 7) return `${limited[0]} (${limited.slice(1, 4)}) ${limited.slice(4)}`;
  if (limited.length <= 9) return `${limited[0]} (${limited.slice(1, 4)}) ${limited.slice(4, 7)} ${limited.slice(7)}`;
  return `${limited[0]} (${limited.slice(1, 4)}) ${limited.slice(4, 7)} ${limited.slice(7, 9)} ${limited.slice(9)}`;
};

/**
 * Telefon numarasından sadece rakamları ayıklar
 * @param phone - Formatlanmış veya ham telefon numarası
 * @returns Sadece rakamlardan oluşan string
 */
export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Türk cep telefonu numarasının geçerli olup olmadığını kontrol eder
 * @param phone - Kontrol edilecek telefon numarası
 * @returns Geçerli ise true, değilse false
 */
export const isValidTurkishMobile = (phone: string): boolean => {
  const cleanPhone = cleanPhoneNumber(phone);
  
  // 11 haneli format: 05xxxxxxxxx
  if (cleanPhone.length === 11 && cleanPhone.startsWith('05')) {
    return true;
  }
  
  // 10 haneli format: 5xxxxxxxxx (0 olmadan)
  if (cleanPhone.length === 10 && cleanPhone.startsWith('5')) {
    return true;
  }
  
  return false;
};

/**
 * Türk sabit hat telefonu numarasının geçerli olup olmadığını kontrol eder
 * @param phone - Kontrol edilecek telefon numarası
 * @returns Geçerli ise true, değilse false
 */
export const isValidTurkishLandline = (phone: string): boolean => {
  const cleanPhone = cleanPhoneNumber(phone);
  
  // Sabit hat formatı: 10 hane (3 haneli alan kodu + 7 hane) veya 11 hane (0 + 3/4 haneli alan kodu + 7 hane)
  if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
    return false;
  }
  
  // 10 haneli durumda (format fonksiyonu 0 eklemeden önce): 3224590790
  if (cleanPhone.length === 10) {
    // İlk 3 hane alan kodu olmalı
    const areaCode = cleanPhone.slice(0, 3);
    
    // Geçerli 3 haneli alan kodları
    const validAreaCodes3 = [
      '212', '216', // İstanbul
      '232', // İzmir
      '312', // Ankara
      '322', // Adana
      '324', // Mersin
      '332', // Konya
      '342', // Gaziantep
      '352', // Kayseri
      '362', // Samsun
      '372', // Zonguldak
      '382', // Trabzon
      '412', // Malatya
      '422', // Diyarbakır
      '432', // Van
      '442', // Erzurum
      '452', // Ordu
      '462', // Rize
      '472', // Artvin
      '482', // Bayburt
      '488' // Gümüşhane
    ];
    
    // 05 ile başlamamalı (cep telefonu değil) ve geçerli alan kodunda olmalı
    if (!cleanPhone.startsWith('05') && validAreaCodes3.includes(areaCode)) {
      return true;
    }
  }
  
  // 11 haneli durumda (format fonksiyonu 0 ekledikten sonra): 03224590790
  if (cleanPhone.length === 11) {
    // 0 ile başlamalı ve 05 ile başlamamalı (cep telefonu değil)
    if (!cleanPhone.startsWith('0') || cleanPhone.startsWith('05')) {
      return false;
    }
    
    // 3 haneli alan kodu kontrolü
    const areaCode3 = cleanPhone.slice(1, 4); // 0'dan sonraki 3 hane
    const validAreaCodes3 = [
      '212', '216', // İstanbul
      '232', // İzmir
      '312', // Ankara
      '322', // Adana
      '324', // Mersin
      '332', // Konya
      '342', // Gaziantep
      '352', // Kayseri
      '362', // Samsun
      '372', // Zonguldak
      '382', // Trabzon
      '412', // Malatya
      '422', // Diyarbakır
      '432', // Van
      '442', // Erzurum
      '452', // Ordu
      '462', // Rize
      '472', // Artvin
      '482', // Bayburt
      '488' // Gümüşhane
    ];
    
    if (validAreaCodes3.includes(areaCode3)) {
      return true;
    }
    
    // 4 haneli alan kodu kontrolü
    const areaCode4 = cleanPhone.slice(1, 5); // 0'dan sonraki 4 hane
    const firstTwoDigits = areaCode4.slice(0, 2);
    if (['02', '03', '04'].includes(firstTwoDigits)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Türk telefon numarasının geçerli olup olmadığını kontrol eder (hem mobil hem sabit hat)
 * @param phone - Kontrol edilecek telefon numarası
 * @returns Geçerli ise true, değilse false
 */
export const isValidTurkishPhone = (phone: string): boolean => {
  return isValidTurkishMobile(phone) || isValidTurkishLandline(phone);
};

/**
 * Telefon alanında backspace işlemini yönetir
 * @param e - KeyboardEvent
 * @param setPhoneNumber - Telefon numarasını güncelleyen fonksiyon
 * @param formSubmitted - Form submit edildi mi
 * @param phoneError - Telefon error state
 * @param setPhoneError - Telefon error state'ini güncelleyen fonksiyon
 * @param validateForm - Opsiyonel: Form validasyon fonksiyonu
 * @returns void
 */
export const handlePhoneKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  setPhoneNumber: (value: string) => void,
  formSubmitted: boolean,
  phoneError: boolean,
  setPhoneError: (value: boolean) => void,
  validateForm?: () => void
): void => {
  if (e.key === 'Backspace') {
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const currentValue = input.value;
    
    // Format karakterleri: boşluk, parantez
    const formatChars = [' ', '(', ')'];
    
    // Cursor format karakterinin önündeyse veya seçim yoksa
    if (cursorPosition > 0 && input.selectionEnd === input.selectionStart) {
      const charBeforeCursor = currentValue[cursorPosition - 1];
      
      if (formatChars.includes(charBeforeCursor)) {
        e.preventDefault();
        
        // Format karakterini atla ve gerçek karakteri bul
        let targetPosition = cursorPosition - 1;
        while (targetPosition > 0 && formatChars.includes(currentValue[targetPosition - 1])) {
          targetPosition--;
        }
        
        if (targetPosition > 0) {
          // Gerçek karakteri sil
          const newValue = currentValue.substring(0, targetPosition - 1) + currentValue.substring(cursorPosition);
          
          // Sadece rakamları al ve yeniden formatla
          const cleanNumber = newValue.replace(/\D/g, '');
          const formatted = formatPhoneNumber(cleanNumber);
          
          setPhoneNumber(formatted);
          
          // Telefon alanı değiştiğinde error durumunu temizle
          if (formSubmitted && phoneError && formatted.trim()) {
            setPhoneError(false);
          }
          
          // Cursor pozisyonunu ayarla
          setTimeout(() => {
            const formattedInput = document.getElementById('phone-input') as HTMLInputElement;
            if (formattedInput) {
              // Silinen karakterden önceki rakam sayısını hesapla
              const digitsBeforeDeleted = currentValue.substring(0, targetPosition - 1).replace(/\D/g, '').length;
              
              // Formatlanmış değerde bu pozisyonu bul
              let newCursorPos = 0;
              let digitCount = 0;
              
              for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) {
                  digitCount++;
                }
                if (digitCount === digitsBeforeDeleted) {
                  newCursorPos = i + 1;
                  break;
                }
              }
              
              formattedInput.setSelectionRange(newCursorPos, newCursorPos);
            }
          }, 0);
        }
      }
    }
  }
};